import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, MousePointer2, Swords, User } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { PathBreadcrumb } from '@/components/game/PathBreadcrumb';
import { supabase } from '@/integrations/supabase/client';
import { Run, Match, Profile, MODE_LABELS, DIFFICULTY_LABELS } from '@/types/game';
import { formatTime } from '@/lib/utils';
import { trackEvent } from '@/lib/analytics';

export default function RunPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState<Run | null>(null);
  const [match, setMatch] = useState<Match | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (runId) {
      fetchRun(runId);
    }
  }, [runId]);

  const fetchRun = async (id: string) => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('runs')
        .select('*, match:matches(*), profile:profiles(*)')
        .eq('id', id)
        .eq('is_public', true)
        .single();

      if (error || !data) {
        navigate('/');
        return;
      }

      setRun({ ...data, path: data.path as string[] || [] } as Run);
      setMatch(data.match as Match);
      setProfile(data.profile as Profile);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBeatRun = () => {
    if (run) {
      trackEvent.beatThisRunClicked(run.id);
      navigate(`/play?beat=${run.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">Loading run...</p>
        </div>
      </div>
    );
  }

  if (!run || !match) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground">Run not found or is private</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-lg mx-auto">
          <div className="game-card">
            {/* Player Info */}
            {profile && (
              <div
                className="flex items-center gap-3 mb-6 cursor-pointer hover:opacity-80"
                onClick={() => profile.is_public && navigate(`/u/${profile.username}`)}
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-semibold">{profile.display_name || profile.username}</p>
                  <p className="text-sm text-muted-foreground">@{profile.username}</p>
                </div>
              </div>
            )}

            {/* Route */}
            <div className="bg-background/50 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-muted-foreground">Route</span>
                <span className={`mode-badge ${match.mode === 'time_attack' ? 'time-attack' : 'under-clicks'}`}>
                  {MODE_LABELS[match.mode]} • {DIFFICULTY_LABELS[match.difficulty]}
                </span>
              </div>
              <div className="flex items-center gap-3 text-lg font-semibold">
                <span>{match.start_title}</span>
                <span className="text-primary">→</span>
                <span className="text-primary">{match.target_title}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="stat-block">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-accent" />
                  <span className="stat-value text-accent">{formatTime(run.duration_ms || 0)}</span>
                </div>
                <p className="stat-label">Time</p>
              </div>
              <div className="stat-block">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <MousePointer2 className="w-4 h-4 text-primary" />
                  <span className="stat-value text-primary">{run.clicks}</span>
                </div>
                <p className="stat-label">Clicks</p>
              </div>
            </div>

            {/* Path */}
            <div className="bg-background/50 rounded-lg p-3 mb-6">
              <p className="text-xs text-muted-foreground mb-2">Path:</p>
              <PathBreadcrumb path={run.path} maxVisible={6} />
            </div>

            {/* Actions */}
            <Button onClick={handleBeatRun} className="w-full gap-2">
              <Swords className="w-4 h-4" />
              Beat This Run
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}